import type { BadgeVariant } from "@/v2-backoffice-client/components/badge"
import {
  LICENSE_STATUS_ACTIVE,
  LICENSE_STATUS_TRIAL,
} from "@/v2-backoffice-client/license/license.constants"

/** lic.LicenseStatuses: Not renewing still bills until its end date. */
const LICENSE_STATUS_NOT_RENEWING = 4

/** The statuses under which a license carries what it grants (lic.LiveLicenses). */
const LIVE_LICENSE_STATUS_CODES = new Set([
  LICENSE_STATUS_TRIAL,
  LICENSE_STATUS_ACTIVE,
  LICENSE_STATUS_NOT_RENEWING,
])

/** Whether a license grants anything right now (Trial, Active, Not renewing). */
export function isLicenseLive(license: { LicenseStatusCode: number }) {
  return LIVE_LICENSE_STATUS_CODES.has(license.LicenseStatusCode)
}

/** One product an add-on goes with, as the add-on reads list them. */
export type AddOnProductRef = { Code: number; Name: string }

/** The shape of a license the dialog holds an add-on's products against. */
export type AddOnLicenseCandidate = {
  Id: number
  ProductCode: number
  ProductName: string
  LicenseStatusCode: number
  SeatsAreAssigned: boolean
}

/**
 * "Goes with a Placepoint MCP (Norway) license" / "Goes with a Placepoint
 * MCP (Norway) or Dataset MCP license" / "Goes with any license": the one
 * sentence that says where an add-on belongs, wherever an add-on is shown.
 */
export function describeAddOnGoesWith(productNames: string | null | undefined) {
  return `Goes with ${describeAddOnLicenseNeeded(productNames)}`
}

/**
 * "a Placepoint MCP (Norway) license" / "a Dataset MCP or Placepoint MCP
 * (Norway) license" / "any license": the license an add-on needs, as a
 * noun phrase for "Goes with …" and "Needs …".
 */
export function describeAddOnLicenseNeeded(
  productNames: string | null | undefined,
) {
  if (!productNames) return "any license"
  const names = productNames.split(", ")
  if (names.length === 1) return `a ${names[0]} license`
  return `a ${names.slice(0, -1).join(", ")} or ${names[names.length - 1]} license`
}

/**
 * The customer's licenses that would carry the add-on, the client-side twin
 * of lic.Get_AddOnCarrierLicenses for the grant dialog: a license of a
 * product the add-on goes with; with no product, every license that reaches
 * everyone (not a seated product).
 */
export function carryingLicensesOf<License extends AddOnLicenseCandidate>(
  addOn: { Products: readonly AddOnProductRef[] },
  licenses: readonly License[],
): License[] {
  if (addOn.Products.length === 0) {
    return licenses.filter((license) => !license.SeatsAreAssigned)
  }
  const codes = new Set(addOn.Products.map((product) => product.Code))
  return licenses.filter((license) => codes.has(license.ProductCode))
}

/**
 * "Placepoint MCP (Norway) license" or "Placepoint license (Placepoint
 * Premium)": how a license is named where the product alone is ambiguous
 * (the Placepoint product has several licenses in the catalog) and where it
 * is not (the MCP servers).
 */
export function describeLicense(license: {
  ProductName: string
  PlanName?: string | null
}) {
  const catalogName =
    license.PlanName && license.PlanName !== license.ProductName
      ? ` (${license.PlanName})`
      : ""
  return `${license.ProductName} license${catalogName}`
}

/** "Unlocks 2 features: A, B" / "Unlocks nothing yet". */
export function describeAddOnUnlocks(addOn: {
  FeatureGroupNames?: string | null
  FeatureCount: number
}) {
  if (!addOn.FeatureGroupNames) {
    return "Unlocks nothing yet: no package is on this add-on"
  }
  return `Unlocks ${addOn.FeatureCount} ${addOn.FeatureCount === 1 ? "feature" : "features"}: ${addOn.FeatureGroupNames}`
}

/** A license the customer holds that an add-on goes with, as the customer's add-on rows carry it. */
export type AddOnCarrier = {
  ProductName: string
  PlanName?: string | null
  LicenseStatusCode: number
  IsLive: boolean
}

/** A held add-on's standing at its customer, as the rows carry it. */
export type HeldAddOnLike = {
  ProductNames?: string | null
  Carriers: readonly AddOnCarrier[]
  HasLiveCarrier: boolean
}

/**
 * Where a held add-on stands at its customer right now, in the words the
 * Add-ons table's Status column and the manage dialog both use: it works
 * (a live license it goes with carries it), the customer holds no license
 * it goes with, or holds one that is not live.
 */
export const ADD_ON_STANDING = {
  works: "Works",
  needsLicense: "Needs a license",
  licenseNotLive: "License not live",
} as const

export type AddOnStanding =
  (typeof ADD_ON_STANDING)[keyof typeof ADD_ON_STANDING]

export function addOnStandingOf(addOn: HeldAddOnLike): AddOnStanding {
  if (addOn.HasLiveCarrier) return ADD_ON_STANDING.works
  if (addOn.Carriers.length === 0) return ADD_ON_STANDING.needsLicense
  return ADD_ON_STANDING.licenseNotLive
}

/** The hue of each standing: green works, amber needs the operator. */
export const ADD_ON_STANDING_VARIANT: Record<AddOnStanding, BadgeVariant> = {
  [ADD_ON_STANDING.works]: "positive",
  [ADD_ON_STANDING.needsLicense]: "warning",
  [ADD_ON_STANDING.licenseNotLive]: "warning",
}

/**
 * The standing as one sentence for a hover: which license carries the
 * add-on, or which license is missing or not live and what to do about it.
 */
export function describeAddOnStanding(
  addOn: HeldAddOnLike,
  who: string,
  statusNameOf: (code: number) => string | undefined,
) {
  switch (addOnStandingOf(addOn)) {
    case ADD_ON_STANDING.works: {
      const live = addOn.Carriers.filter((carrier) => carrier.IsLive)
      return `Works for the people on the ${live.map(describeLicense).join(" and the ")} while it is live, and stops with it.`
    }
    case ADD_ON_STANDING.needsLicense:
      return `${describeAddOnGoesWith(addOn.ProductNames)}. ${who} has no such license, so this add-on gives nothing yet. Grant the license and the add-on starts working with it.`
    case ADD_ON_STANDING.licenseNotLive:
      return `${who}'s ${addOn.Carriers.map((carrier) => `${describeLicense(carrier)} is ${statusNameOf(carrier.LicenseStatusCode)?.toLowerCase() ?? "not live"}`).join(" and ")}, so the add-on gives nothing right now. It starts working again when that license is live.`
  }
}
